import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, Phone, RefreshCw, LogOut, Inbox, MessageSquare, FileText, Send } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_OPTIONS = ["new", "contacted", "won", "lost"];
const STATUS_COLOR = {
  new: "text-[#22C55E] border-[#22C55E]/40",
  contacted: "text-[#1E90FF] border-[#1E90FF]/40",
  won: "text-[#22C55E] border-[#22C55E]/40",
  lost: "text-white/40 border-white/20",
};

export default function AdminDashboard() {
  const [token, setToken] = useState(localStorage.getItem("admin_token") || "");
  const [authed, setAuthed] = useState(false);
  const [input, setInput] = useState("");
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(false);
  const [digestLoading, setDigestLoading] = useState(false);

  const headers = useCallback(() => ({ "X-Admin-Token": token }), [token]);

  const sendDigest = async () => {
    setDigestLoading(true);
    try {
      const res = await axios.post(`${API}/admin/send-digest`, {}, { headers: headers() });
      if (res.data.sent) toast.success(`Digest emailed — ${res.data.count} lead(s) in the last 24h.`);
      else toast.message(res.data.reason === "no new leads" ? "No new leads in the last 24 hours." : `Digest not sent: ${res.data.reason}`);
    } catch (e) {
      toast.error("Couldn't send digest.");
    } finally {
      setDigestLoading(false);
    }
  };

  const loadLeads = useCallback(async (tok) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/leads`, { headers: { "X-Admin-Token": tok || token } });
      setLeads(res.data);
      setAuthed(true);
    } catch (e) {
      if (e.response?.status === 401) {
        toast.error("Invalid admin token");
        setAuthed(false);
        localStorage.removeItem("admin_token");
      } else toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (token) loadLeads(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (e) => {
    e.preventDefault();
    setToken(input);
    localStorage.setItem("admin_token", input);
    await loadLeads(input);
  };

  const logout = () => {
    localStorage.removeItem("admin_token");
    setToken(""); setAuthed(false); setLeads([]);
  };

  const updateStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/leads/${id}?status=${status}`, {}, { headers: headers() });
      setLeads((ls) => ls.map((l) => (l.id === id ? { ...l, status } : l)));
    } catch { toast.error("Update failed"); }
  };

  if (!authed) {
    return (
      <div className="min-h-screen grid place-items-center bg-[#0a0a0a] px-5">
        <form onSubmit={login} className="w-full max-w-sm border border-white/10 bg-[#0d0d0d] p-8" data-testid="admin-login-form">
          <h1 className="font-display font-black text-2xl tracking-tighter mb-1">Admin Access</h1>
          <p className="text-white/50 text-sm mb-6">Enter your admin token to view leads.</p>
          <input type="password" value={input} onChange={(e) => setInput(e.target.value)} placeholder="Admin token"
            className="w-full bg-black border border-white/15 rounded-sm px-4 py-3 text-white placeholder-white/30 focus:border-[#1E90FF] focus:outline-none mb-4"
            data-testid="admin-token-input" />
          <button type="submit" className="w-full bg-[#22C55E] text-black font-semibold py-3 rounded-sm hover:bg-[#1fb455] transition-colors" data-testid="admin-login-button">
            View Dashboard
          </button>
        </form>
      </div>
    );
  }

  const stats = {
    total: leads.length,
    chatbot: leads.filter((l) => l.source === "chatbot").length,
    form: leads.filter((l) => l.source === "contact_form").length,
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-white">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur-xl sticky top-0 z-10">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <div className="flex items-baseline gap-1">
            <span className="font-display font-black text-xl text-[#1E90FF]">360</span>
            <span className="font-display font-light text-xl">LEADS</span>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={sendDigest} disabled={digestLoading} className="text-white/60 hover:text-white flex items-center gap-2 text-sm disabled:opacity-50" data-testid="admin-send-digest"><Send size={16} /> {digestLoading ? "Sending..." : "Send Digest"}</button>
            <button onClick={() => loadLeads()} className="text-white/60 hover:text-white flex items-center gap-2 text-sm" data-testid="admin-refresh"><RefreshCw size={16} /> Refresh</button>
            <button onClick={logout} className="text-white/60 hover:text-white flex items-center gap-2 text-sm" data-testid="admin-logout"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <div className="grid grid-cols-3 gap-px bg-white/10 border border-white/10 mb-10">
          {[{ icon: Inbox, label: "Total Leads", val: stats.total }, { icon: MessageSquare, label: "From Chatbot", val: stats.chatbot }, { icon: FileText, label: "From Form", val: stats.form }].map((s) => {
            const Icon = s.icon;
            return (
              <div key={s.label} className="bg-[#0d0d0d] p-6">
                <Icon className="text-[#1E90FF] mb-3" size={20} />
                <p className="font-display font-black text-3xl">{s.val}</p>
                <p className="text-white/50 text-sm">{s.label}</p>
              </div>
            );
          })}
        </div>

        <h2 className="font-display font-bold text-xl mb-5">All Leads</h2>
        {loading && <p className="text-white/40">Loading…</p>}
        {!loading && leads.length === 0 && <p className="text-white/40">No leads yet. They'll appear here as visitors reach out.</p>}

        <div className="space-y-3" data-testid="admin-leads-list">
          {leads.map((l) => (
            <div key={l.id} className="border border-white/10 bg-[#0d0d0d] p-5 flex flex-col md:flex-row md:items-center gap-4" data-testid={`lead-row-${l.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 flex-wrap">
                  <span className="font-semibold">{l.name || "Website Visitor"}</span>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${l.source === "chatbot" ? "text-[#1E90FF] border-[#1E90FF]/40" : "text-white/50 border-white/20"}`}>{l.source === "chatbot" ? "Chatbot" : "Form"}</span>
                  {l.service && <span className="text-xs text-white/40">· {l.service}</span>}
                  {l.email_status && (
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${l.email_status === "sent" ? "text-[#22C55E] border-[#22C55E]/40" : l.email_status === "failed" ? "text-red-400 border-red-400/40" : "text-white/40 border-white/20"}`} title={l.email_detail || ""}>
                      {l.email_status === "sent" ? "Emailed" : l.email_status === "failed" ? "Email failed" : "Email off"}
                    </span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2 text-sm text-white/60">
                  {l.email && <span className="flex items-center gap-1.5"><Mail size={13} /> {l.email}</span>}
                  {l.phone && <span className="flex items-center gap-1.5"><Phone size={13} /> {l.phone}</span>}
                </div>
                {l.message && <p className="text-white/50 text-sm mt-2 line-clamp-3 whitespace-pre-wrap">{l.message}</p>}
                <p className="text-white/30 text-xs mt-2">{new Date(l.created_at).toLocaleString()}</p>
              </div>
              <select value={l.status} onChange={(e) => updateStatus(l.id, e.target.value)}
                className={`bg-black border rounded-sm px-3 py-2 text-sm capitalize ${STATUS_COLOR[l.status] || "text-white border-white/20"}`}
                data-testid={`lead-status-${l.id}`}>
                {STATUS_OPTIONS.map((s) => <option key={s} value={s} className="text-white bg-black capitalize">{s}</option>)}
              </select>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
